import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import TroikaText3D from './Text3D';

interface ProcessedObject
{
	objects: THREE.Mesh[];
	depth: number;
}

export default class OutlineManager
{
	private outlineMaterial: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });

	private getParentDepth(object: THREE.Object3D): number
	{
		let depth = 0;
		let current = object.parent;
		while (current && current.parent && current.name !== 'root')
		{
			depth++;
			current = current.parent;
		}
		return depth;
	}
  
	processObjects(objects: THREE.Object3D[]): THREE.Mesh[]
	{
		const processedObjects: Map<THREE.Object3D, ProcessedObject> = new Map();
		const parents: Map<THREE.Mesh, number> = new Map();
		const roots: Map<THREE.Object3D, ProcessedObject> = new Map();
		const passthroughObjects: THREE.Object3D[] = [];
		const passthroughTypes: (typeof THREE.Object3D)[] = [TroikaText3D];
		if (!objects.length) return [];

		// Process all valid objects
		for (const object of objects)
		{
			// Skip if object has no parent
			// (this should never happen as all parents are parented to the root)
			if (!object.parent) continue;
			// If object is not a mesh or is a model root, combine children
			if (passthroughTypes.includes(object.constructor as typeof THREE.Object3D))
			{
				passthroughObjects.push(object);
			}
			else if (!(object instanceof THREE.Mesh) || object.userData.isModelRoot)
			{
				const subMeshes = new Array<THREE.Mesh>();
				object.traverse((child) => 
				{
					if (!child.userData.hideInHierarchy && child instanceof THREE.Mesh)
					{
						subMeshes.push(child);
					}
				});
				roots.set(object, {
					objects: subMeshes,
					depth: this.getParentDepth(object) || 0
				});
			}
			// if object has children, add to parents map and retrieve depth
			else if (object.children.length)
			{
				parents.set(object, this.getParentDepth(object));
			}
			// merge children that share a parent
			// if object has a parent that isn't acknowledged, add to processed objects under parent
			else if (!processedObjects.has(object.parent))
			{
				processedObjects.set(object.parent, {
					objects: [object],
					depth: this.getParentDepth(object) || 0
				});
			}
			// otherwise if parent exists in processed objects, add to parent's objects
			else
			{
				processedObjects.get(object.parent)!.objects.push(object);
			}
		}

		const returnMeshes: {mesh: THREE.Mesh, depth: number}[] = [...passthroughObjects.map(o => ({mesh: o as THREE.Mesh, depth: 0}))];
		const processedObjectsArray = Array.from(processedObjects.values());
		const rootsArray = Array.from(roots.values());
		for (const object of processedObjectsArray)
		{
			returnMeshes.push({
				mesh: new THREE.Mesh(this.mergeObjects(object.objects), this.outlineMaterial), 
				depth: object.depth
			});
		}
		for (const [parent, depth] of parents.entries())
		{
			returnMeshes.push({
				mesh: new THREE.Mesh(this.mergeObjects([parent]), this.outlineMaterial),
				depth: depth
			});
		}
		for (const object of rootsArray)
		{
			returnMeshes.push({
				mesh: new THREE.Mesh(this.mergeObjects(object.objects), this.outlineMaterial),
				depth: object.depth
			});
		}

		return returnMeshes.sort((a, b) => b.depth - a.depth).map(o => o.mesh);
	}

	mergeObjects(objects: THREE.Mesh[]): THREE.BufferGeometry
	{
		const geometries: THREE.BufferGeometry[] = [];
		
		for (const object of objects)
		{
			if (!object.geometry)
			{
				console.warn('OutlineManager: Object missing geometry, skipping:', object);
				continue;
			}

			// Check if material is visible
			const isMaterialVisible = Array.isArray(object.material) 
				? object.material.some(m => m.visible)
				: object.material.visible;

			if (!isMaterialVisible)
			{
				continue;
			}

			// Clone the geometry to avoid modifying the original
			const geometry = object.geometry.clone();
			
			// Get required attributes
			const position = geometry.getAttribute('position');
			const index = geometry.index;
			
			if (!position)
			{
				console.warn('OutlineManager: Object missing position attribute, skipping:', object);
				continue;
			}

			if (!index)
			{
				console.warn('OutlineManager: Object missing index, skipping:', object);
				continue;
			}

			// Create a new geometry with only position and index
			const cleanGeometry = new THREE.BufferGeometry();
			
			// Apply object's transform to the position attribute
			const positions = position.array;
			const transformedPositions = new Float32Array(positions.length);
			const matrix = object.matrixWorld;
			
			for (let i = 0; i < positions.length; i += 3)
			{
				const x = positions[i];
				const y = positions[i + 1];
				const z = positions[i + 2];
				
				// Apply the transform matrix to each vertex
				const vector = new THREE.Vector3(x, y, z).applyMatrix4(matrix);
				transformedPositions[i] = vector.x;
				transformedPositions[i + 1] = vector.y;
				transformedPositions[i + 2] = vector.z;
			}

			cleanGeometry.setAttribute('position', new THREE.BufferAttribute(
				transformedPositions,
				position.itemSize,
				position.normalized
			));
			
			if (index)
			{
				cleanGeometry.setIndex(index);
			}

			geometries.push(cleanGeometry);
		}

		if (geometries.length === 0)
		{
			return new THREE.BufferGeometry();
		}

		try
		{
			return mergeGeometries(geometries);
		}
		catch (error)
		{
			console.error('OutlineManager: Failed to merge geometries:', error);
			return new THREE.BufferGeometry();
		}
	}
}